/**
 * HeaderRenderer — renders <colgroup> + <thead> for DynamicTableV2.
 *
 * Width contract:
 *  - <colgroup> is the SINGLE source of truth for column widths.
 *  - Each <col> gets width={col.width.default} and style={{ minWidth, maxWidth }}.
 *  - <th> and <td> cells NEVER repeat width / minWidth / maxWidth inline styles;
 *    they inherit from the <col> via table-layout: fixed on the parent <table>.
 *
 * Alignment contract:
 *  - col.align ('left' | 'center' | 'right') drives text-align on every <th>.
 *  - Body cells read the same col.align — never set it ad-hoc per cell.
 *
 * Reorder contract:
 *  - When `reorderable` is true, columns shift live during drag (the actual
 *    column order updates on every drag position change, not just on drop).
 *  - Only the drag-handle icon is the drag handle; the rest of the <th> remains
 *    clickable for sort. The icon is absolutely positioned so it takes zero
 *    layout space (no text offset when hidden).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type {
  DropResult,
  DragStart,
  DragUpdate,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { Typography } from '@packages/ui/primitives/Typography';
import { Checkbox } from '@packages/ui/primitives/Checkbox';
import { DataTable } from '@packages/ui/patterns/DataTable';
import type { InternalColumnDef } from '../schema/types';
import { getPinnedBoundaryShadow, type PinOffsets } from './computePinOffsets';
import { TABLE_IDS } from '../semantic-contract';

const SortableHeaderCell = DataTable.SortableHeader as React.FC<React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }>;
const ResizeHandle = DataTable.ResizeHandle as React.FC<React.HTMLAttributes<HTMLDivElement>>;

const CHECKBOX_COL_WIDTH = 40;
const ACTIONS_COL_WIDTH = 80;

const EXPAND_COL_WIDTH = 45;

export interface HeaderRendererProps {
  columns: ReadonlyArray<InternalColumnDef>;
  sortState: { field: string; direction: 'asc' | 'desc' } | null;
  onSort: (field: string) => void;
  showCheckbox: boolean;
  onSelectAll: (checked: boolean) => void;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  showActionsColumn: boolean;
  reorderable?: boolean;
  onColumnReorder?: (sourceIndex: number, destinationIndex: number) => void;
  resizable?: boolean;
  columnSizes?: Record<string, number>;
  onColumnResize?: (field: string, width: number) => void;
  onColumnResizeEnd?: (field: string, width: number) => void;
  pinOffsets?: PinOffsets;
  isGroupingEnabled?: boolean;
  /** Extra invisible <col> entries to accommodate sub-rows with more columns than the parent. */
  subRowOverflowCols?: number;
}

function ColumnHeader({
  col,
  sortState,
  onSort,
}: {
  col: InternalColumnDef;
  sortState: HeaderRendererProps['sortState'];
  onSort: (field: string) => void;
}): JSX.Element {
  return (
    <SortableHeaderCell
      className={`flex items-center custom-header--main ${
        col.align === 'right'
          ? 'justify-end'
          : col.align === 'center'
          ? 'justify-center'
          : 'justify-start'
      }`}>
      <Typography variant='body-s'>{col.header}</Typography>
      {col.sortable && (
        <div className='flex flex-col sorting-icon'>
          <span
            className={`material-symbols-rounded ${
              sortState?.field === col.field && sortState.direction === 'asc' ? 'active' : ''
            }`}>
            arrow_drop_up
          </span>
          <span
            className={`material-symbols-rounded ${
              sortState?.field === col.field && sortState.direction === 'desc' ? 'active' : ''
            }`}>
            arrow_drop_down
          </span>
        </div>
      )}
    </SortableHeaderCell>
  );
}

/**
 * Single draggable <th> with a separated drag handle.
 * - `dragHandleProps` goes ONLY to the icon span (not the whole <th>)
 * - The <th> onClick still fires sort normally
 * - Icon is absolutely positioned: zero layout impact when hidden
 * - Portal rendering when dragging preserves width
 */
function DraggableTh({
  col,
  index,
  sortState,
  onSort,
  thStyle,
  resizable,
  currentWidth,
  onResize,
  onResizeEnd,
}: {
  col: InternalColumnDef;
  index: number;
  sortState: HeaderRendererProps['sortState'];
  onSort: (field: string) => void;
  thStyle: React.CSSProperties;
  resizable?: boolean;
  currentWidth?: number;
  onResize?: (field: string, width: number) => void;
  onResizeEnd?: (field: string, width: number) => void;
}) {
  const widthRef = useRef<number>(0);

  const setRef = useCallback((el: HTMLTableCellElement | null, provided: DraggableProvided) => {
    provided.innerRef(el);
    if (el) widthRef.current = el.getBoundingClientRect().width;
  }, []);

  const isPinned = col.pinned != null;

  return (
    <Draggable draggableId={col.id} index={index} isDragDisabled={isPinned}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
        const dragging = snapshot.isDragging;

        const cellStyle: React.CSSProperties = {
          ...thStyle,
          ...(isPinned ? {} : { position: 'relative' as const }),
          ...(dragging
            ? {
                background: '#eef2ff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderRadius: 6,
                width: widthRef.current || 'auto',
                display: 'table-cell',
                zIndex: 9999,
              }
            : {}),
          ...(isPinned ? {} : provided.draggableProps.style),
        };

        const cellContent = (
          <th
            ref={(el) => setRef(el, provided)}
            {...provided.draggableProps}
            style={cellStyle}
            onClick={col.sortable ? () => onSort(col.field) : undefined}
            data-testid={`dt-v2-header-${col.field}`}
            className='dt-v2-reorderable-th'>
            {!isPinned && (
              <span
                {...provided.dragHandleProps}
                className='material-symbols-rounded dt-v2-drag-handle'
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '7px',
                  bottom: 0,
                  fontSize: 14,
                  lineHeight: 1,
                  color: '#94a3b8',
                  width: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'grab',
                  opacity: 0,
                  transition: 'opacity 150ms ease',
                  zIndex: 1,
                }}>
                drag_indicator
              </span>
            )}
            {isPinned && <span {...provided.dragHandleProps} style={{ display: 'none' }} />}
            <div
              className='dt-v2-header-content'
              style={{ paddingLeft: 0, transition: 'padding-left 150ms ease' }}>
              <ColumnHeader col={col} sortState={sortState} onSort={onSort} />
            </div>
            {resizable && onResize && (
              <ResizeHandleCell
                col={col}
                currentWidth={currentWidth ?? col.width.default}
                onResize={onResize}
                onResizeEnd={onResizeEnd}
              />
            )}
          </th>
        );

        if (dragging) {
          return ReactDOM.createPortal(cellContent, document.body);
        }
        return cellContent;
      }}
    </Draggable>
  );
}

/**
 * Injects hover styles for the drag handle icon and column highlight during drag.
 */
function useDragStyles(canDrag: boolean, draggingField: string | null) {
  useEffect(() => {
    if (!canDrag) return;

    const id = 'dt-v2-reorder-styles';
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const rules: string[] = [
      `.dt-v2-reorderable-th:hover .dt-v2-drag-handle { opacity: 0.7 !important; }`,
      `.dt-v2-reorderable-th:hover .dt-v2-header-content { padding-left: 16px !important; }`,
      `.dt-v2-reorderable-th:active .dt-v2-drag-handle { opacity: 1 !important; }`,
    ];

    if (draggingField) {
      rules.push(
        `[data-testid$="-${CSS.escape(
          draggingField
        )}"] { background: rgba(99,102,241,0.06) !important; }`
      );
    }

    const styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.textContent = rules.join('\n');
    document.head.appendChild(styleEl);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [canDrag, draggingField]);
}

/**
 * Resize handle pointer interaction. Uses setPointerCapture for reliable
 * cross-element dragging. Fires onResize on every move frame and
 * onResizeEnd on pointer up.
 */
function ResizeHandleCell({
  col,
  currentWidth,
  onResize,
  onResizeEnd,
}: {
  col: InternalColumnDef;
  currentWidth: number;
  onResize: (field: string, width: number) => void;
  onResizeEnd?: (field: string, width: number) => void;
}) {
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = currentWidth;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      target.setAttribute('data-resizing', 'true');

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        const newWidth = Math.max(col.width.min, Math.min(col.width.max, startWidth + delta));
        onResize(col.field, newWidth);
      };

      const onUp = (ev: PointerEvent) => {
        target.releasePointerCapture(ev.pointerId);
        target.removeAttribute('data-resizing');
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        const delta = ev.clientX - startX;
        const finalWidth = Math.max(col.width.min, Math.min(col.width.max, startWidth + delta));
        onResizeEnd?.(col.field, finalWidth);
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
    },
    [col.field, col.width.min, col.width.max, currentWidth, onResize, onResizeEnd]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onResize(col.field, col.width.default);
      onResizeEnd?.(col.field, col.width.default);
    },
    [col.field, col.width.default, onResize, onResizeEnd]
  );

  const stopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <ResizeHandle
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
      onClick={stopClick}
      data-testid={`dt-v2-resize-${col.field}`}
    />
  );
}

export const HeaderRenderer: React.FC<HeaderRendererProps> = ({
  columns,
  sortState,
  onSort,
  showCheckbox,
  onSelectAll,
  isAllSelected,
  isPartiallySelected,
  showActionsColumn,
  reorderable = false,
  onColumnReorder,
  resizable = false,
  columnSizes,
  onColumnResize,
  onColumnResizeEnd,
  pinOffsets,
  isGroupingEnabled = false,
  subRowOverflowCols = 0,
}) => {
  const hasGroups = columns.some((col) => col.group !== null);
  const groups = hasGroups ? getColumnGroups(columns) : null;
  const canDrag = reorderable && !hasGroups && onColumnReorder != null;

  const [draggingField, setDraggingField] = useState<string | null>(null);
  const dragSourceRef = useRef<number | null>(null);

  useDragStyles(canDrag, draggingField);

  const handleDragStart = useCallback(
    (start: DragStart) => {
      const col = columns[start.source.index];
      if (col) setDraggingField(col.field);
      dragSourceRef.current = start.source.index;
    },
    [columns]
  );

  const handleDragUpdate = useCallback(
    (update: DragUpdate) => {
      if (!onColumnReorder || dragSourceRef.current == null) return;
      const dest = update.destination;
      if (!dest || dest.index === dragSourceRef.current) return;

      onColumnReorder(dragSourceRef.current, dest.index);
      dragSourceRef.current = dest.index;
    },
    [onColumnReorder]
  );

  const handleDragEnd = useCallback((_result: DropResult) => {
    setDraggingField(null);
    dragSourceRef.current = null;
  }, []);

  const canResize = resizable && onColumnResize != null;

  const renderColgroup = () => (
    <colgroup>
      {showCheckbox && (
        <col
          style={{
            width: CHECKBOX_COL_WIDTH,
            minWidth: CHECKBOX_COL_WIDTH,
            maxWidth: CHECKBOX_COL_WIDTH,
          }}
        />
      )}
      {isGroupingEnabled && (
        <col
          style={{
            width: EXPAND_COL_WIDTH,
            minWidth: EXPAND_COL_WIDTH,
            maxWidth: EXPAND_COL_WIDTH,
          }}
        />
      )}
      {columns.map((col) => (
        <col
          key={col.id}
          style={{
            width: columnSizes?.[col.field] ?? col.width.default,
            minWidth: col.width.min,
            maxWidth: col.width.max,
          }}
        />
      ))}
      {subRowOverflowCols > 0 &&
        Array.from({ length: subRowOverflowCols }, (_, i) => (
          <col key={`sub-overflow-${i}`} style={{ width: 0, minWidth: 0, maxWidth: 0 }} />
        ))}
      {showActionsColumn && (
        <col
          style={{
            width: ACTIONS_COL_WIDTH,
            minWidth: ACTIONS_COL_WIDTH,
            maxWidth: ACTIONS_COL_WIDTH,
          }}
        />
      )}
    </colgroup>
  );

  const selectAllRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isPartiallySelected && !isAllSelected;
    }
  }, [isPartiallySelected, isAllSelected]);

  const hasLeftPinned = pinOffsets != null && pinOffsets.left.size > 0;

  const checkboxStickyStyle: React.CSSProperties = hasLeftPinned
    ? { position: 'sticky', left: 0, top: 0, zIndex: 3, backgroundColor: '#ffffff' }
    : {};

  const renderCheckboxTh = () =>
    !groups && showCheckbox ? (
      <th
        data-testid='dt-v2-header-checkbox'
        style={{ paddingLeft: 8, verticalAlign: 'middle', ...checkboxStickyStyle }}>
        <Checkbox
          ref={selectAllRef}
          checked={isAllSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
          semanticId={TABLE_IDS.selectAll}
        />
      </th>
    ) : null;

  const hasPinnedCols = hasLeftPinned || (pinOffsets != null && pinOffsets.right.size > 0);

  const thStyle = (col: InternalColumnDef): React.CSSProperties => {
    const shadow = pinOffsets ? getPinnedBoundaryShadow(col, columns as InternalColumnDef[]) : null;

    const base: React.CSSProperties = {
      textAlign: col.align === 'center' ? 'center' : col.align === 'right' ? 'right' : 'left',
      cursor: col.sortable ? 'pointer' : undefined,
      overflow: 'hidden',
    };

    if (hasPinnedCols && col.pinned != null) {
      const leftOffset = pinOffsets!.left.get(col.field);
      const rightOffset = pinOffsets!.right.get(col.field);
      return {
        ...base,
        position: 'sticky',
        top: 0,
        ...(leftOffset != null ? { left: leftOffset } : {}),
        ...(rightOffset != null ? { right: rightOffset } : {}),
        zIndex: 3,
        backgroundColor: '#ffffff',
        ...(shadow ? { boxShadow: shadow } : {}),
      };
    }

    return {
      ...base,
      ...(canResize ? { position: 'relative' as const } : {}),
    };
  };

  if (!canDrag) {
    return (
      <>
        {renderColgroup()}
        <thead>
          {groups && (
            <tr>
              {showCheckbox && <th rowSpan={2} />}
              {groups.map((group, gi) => (
                <th
                  key={group.name ?? `ungrouped-${gi}`}
                  colSpan={group.columns.length}
                  style={{ textAlign: 'left' }}
                  data-testid={`dt-v2-group-${group.name ?? 'ungrouped'}`}>
                  {group.name ?? ''}
                </th>
              ))}
              {subRowOverflowCols > 0 &&
                Array.from({ length: subRowOverflowCols }, (_, i) => (
                  <th key={`sub-overflow-grp-${i}`} rowSpan={2} style={{ width: 0, padding: 0 }} />
                ))}
              {showActionsColumn && <th rowSpan={2} />}
            </tr>
          )}
          <tr>
            {renderCheckboxTh()}
            {isGroupingEnabled && <th style={{ minWidth: EXPAND_COL_WIDTH, width: EXPAND_COL_WIDTH }} />}
            {columns.map((col) => (
              <th
                key={col.id}
                style={thStyle(col)}
                onClick={col.sortable ? () => onSort(col.field) : undefined}
                data-testid={`dt-v2-header-${col.field}`}>
                <ColumnHeader col={col} sortState={sortState} onSort={onSort} />
                {canResize && (
                  <ResizeHandleCell
                    col={col}
                    currentWidth={columnSizes?.[col.field] ?? col.width.default}
                    onResize={onColumnResize!}
                    onResizeEnd={onColumnResizeEnd}
                  />
                )}
              </th>
            ))}
            {subRowOverflowCols > 0 &&
              Array.from({ length: subRowOverflowCols }, (_, i) => (
                <th key={`sub-overflow-th-${i}`} style={{ width: 0, padding: 0 }} />
              ))}
            {!groups && showActionsColumn && <th data-testid='dt-v2-header-actions' />}
          </tr>
        </thead>
      </>
    );
  }

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}>
      {renderColgroup()}
      <Droppable droppableId='dt-v2-header-columns' direction='horizontal'>
        {(droppableProvided) => (
          <thead>
            <tr ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
              {renderCheckboxTh()}
              {isGroupingEnabled && <th style={{ minWidth: EXPAND_COL_WIDTH, width: EXPAND_COL_WIDTH }} />}
              {columns.map((col, index) => (
                <DraggableTh
                  key={col.id}
                  col={col}
                  index={index}
                  sortState={sortState}
                  onSort={onSort}
                  thStyle={thStyle(col)}
                  resizable={canResize}
                  currentWidth={columnSizes?.[col.field] ?? col.width.default}
                  onResize={onColumnResize}
                  onResizeEnd={onColumnResizeEnd}
                />
              ))}
              {droppableProvided.placeholder}
              {subRowOverflowCols > 0 &&
                Array.from({ length: subRowOverflowCols }, (_, i) => (
                  <th key={`sub-overflow-th-${i}`} style={{ width: 0, padding: 0 }} />
                ))}
              {showActionsColumn && <th data-testid='dt-v2-header-actions' />}
            </tr>
          </thead>
        )}
      </Droppable>
    </DragDropContext>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getColumnGroups(
  columns: ReadonlyArray<InternalColumnDef>
): Array<{ name: string | null; columns: InternalColumnDef[] }> {
  const groups: Array<{ name: string | null; columns: InternalColumnDef[] }> = [];
  let current: { name: string | null; columns: InternalColumnDef[] } | null = null;

  for (const col of columns) {
    if (!current || current.name !== col.group) {
      current = { name: col.group, columns: [col as InternalColumnDef] };
      groups.push(current);
    } else {
      current.columns.push(col as InternalColumnDef);
    }
  }

  return groups;
}
